import { useUpdateAssessor } from "@/api/console/assessor/useUpdateAssessor";
import InputGroup from "@/components/InputGroup";
import Select from "@/components/Select";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { emailPattern } from "@/lib/regexPattern";
import toast from "@/lib/toast";
import {
  AssessorUpdate,
  assessorUpdateSchema,
} from "@/schema/console/assessors/AssessorUpdate";
import useAssessorDialog from "@/src/console/(auth)/assessors/_hooks/useAssessorDialog";
import { SpinnerIosRegular } from "@fluentui/react-icons";
import { zodResolver } from "@hookform/resolvers/zod";
import { useController, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { mutate } from "swr";

function UpdateAssessorDialog() {
  const { t } = useTranslation();
  const { assessor, type, resetDialog } = useAssessorDialog();
  const assessorId = assessor?.id ?? -1;

  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<AssessorUpdate>({
    resolver: zodResolver(assessorUpdateSchema),
    values: assessor,
  });
  const { trigger, isMutating } = useUpdateAssessor(assessorId, {
    onSuccess: (data) => {
      toast.success(data.message);
      mutate("/console/admin_users");
      resetDialog();
    },
  });

  const { field: roleField } = useController({
    control,
    name: "role",
  });

  return (
    <div>
      <Dialog
        open={type === "update"}
        onOpenChange={(open) => !open && resetDialog()}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("update-assessor")}</DialogTitle>
          </DialogHeader>
          <form
            onSubmit={(event) => {
              event.preventDefault();
              handleSubmit((data) => {
                trigger(data);
              })();
            }}
          >
            <div className="flex flex-col gap-6 px-6 pb-5 pt-4">
              <InputGroup
                label={t("name")}
                name="name"
                required
                error={errors.name?.message}
                register={register("name", {
                  required: t("name-required"),
                })}
              />
              <InputGroup
                label={t("email")}
                name="email"
                required
                error={errors.email?.message}
                register={register("email", {
                  required: t("email-required"),
                  pattern: {
                    value: emailPattern,
                    message: t("enter-mail-message"),
                  },
                })}
                className="col-span-2"
                type="email"
              />
              <div>
                <div className="mb-1">{t("role")}</div>
                <Select
                  placeholder="Select role"
                  className="col-span-2 rounded-md border"
                  value={`${roleField.value}`}
                  onChange={(value) => roleField.onChange(value)}
                  options={[
                    { value: "0", label: t("admin") },
                    { value: "1", label: t("assessor") },
                  ]}
                />
                {errors.role && (
                  <p className="text-red-500">{errors.role.message}</p>
                )}
              </div>

              <Button
                isLoading={isMutating}
                LoadingComponent={
                  <SpinnerIosRegular className="animate-spin" />
                }
                type="submit"
              >
                {t("update")}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default UpdateAssessorDialog;
